import { useRouter } from 'next/router';
import { useCallback, useContext, useEffect } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  RefsContext,
  useGetRef,
  useStepValidation,
} from '@zurich/common/contexts';
import { findTopElement } from '@zurich/common/forms';
import { useBasePath, useScrollIntoView } from '@zurich/common/hooks';
import { alwaysValidValidationSelector } from '@zurich/common/recoil';
import { vsgaStepConfig } from '~/components/stepConfig';
import { activeStepAtom, scrollToFieldAtom, vsgaPayloadAtom } from '~/state';
import { usePostVSGAData } from './api';

/**
 * `useStepper` is used to handle the validation and actions
 * when navigating forwards and backwards through the VSGA flow.
 *
 */
export function useStepper() {
  const router = useRouter();
  const { withBasePath } = useBasePath();
  const { stepValidation, triggerStepValidation, resetStepValidation } =
    useStepValidation();
  const { fetch, dataState } = usePostVSGAData();

  const [payload, setPayload] = useRecoilState(vsgaPayloadAtom);

  const [activeStep, setActiveStep] = useRecoilState(activeStepAtom);
  const currentStep = vsgaStepConfig?.[activeStep];
  const isNotLastStep = activeStep < vsgaStepConfig.length - 1;

  // get validation for current step
  const validatorMap = vsgaStepConfig.map(
    ({ validationSelector }) => validationSelector
  );

  const activeStepValidation = useRecoilValue(
    // if no validationSelector is provided, disable validation
    validatorMap[activeStep] || alwaysValidValidationSelector
  );

  const setScrollTo = useSetRecoilState(scrollToFieldAtom);
  const fieldName = useRecoilValue(scrollToFieldAtom);
  const { refs } = useContext(RefsContext);
  const ref = useGetRef(fieldName);
  const scrollTo = useScrollIntoView(ref);

  const { id } = router.query;
  const idFromUrl: string | undefined = typeof id === 'string' ? id : undefined;

  // persist payload atom
  useEffect(() => {
    if (id && payload.id !== id) {
      setPayload((prev) => ({
        ...prev,
        id: idFromUrl,
      }));
    }
  }, [idFromUrl, setPayload]);

  // actually scroll to field if triggered by fieldName
  useEffect(() => {
    if (fieldName !== null && stepValidation > 0) scrollTo();
  }, [fieldName, scrollTo, stepValidation]);

  const scrollToErrorLocal = useCallback(() => {
    if (Array.isArray(activeStepValidation.errors)) {
      return;
    }
    const [topElement] = findTopElement(refs, activeStepValidation.errors, 0);
    if (topElement) {
      setScrollTo(topElement);
    }
    return topElement;
  }, [refs, activeStepValidation.errors, setScrollTo]);

  const handleNext = async () => {
    if (activeStepValidation?.valid === true) {
      if (currentStep?.onNext) {
        await vsgaStepConfig[activeStep].onNext!();
      }

      if (isNotLastStep) {
        // Uncomment once BE ready
        void router.replace(
          `/vsga?step=${vsgaStepConfig?.[activeStep + 1].stepName}&id=${payload.id}`
        );
        setActiveStep((step) => step + 1);
        resetStepValidation();
        window?.scrollTo({
          behavior: 'smooth',
          top: 0,
        });
      }
      // last step should trigger the save action if valid!
      if (activeStep === vsgaStepConfig.length - 1) {
        await fetch({
          params: {
            body: payload,
          },
          onSuccess: () => {
            window.location.replace(withBasePath('/vsga/success'));
            window.sessionStorage.clear();
          },
        });
      }
    } else {
      triggerStepValidation();
      scrollToErrorLocal();
    }
  };

  const handlePrev = () => {
    if (activeStep > 0) {
      setActiveStep((step) => step - 1);
      // Uncomment once BE ready
      void router.replace(
        `/vsga?step=${vsgaStepConfig?.[activeStep - 1].stepName}&id=${payload.id}`
      );
      resetStepValidation();
    }
  };

  return { handleNext, handlePrev, dataState, stepValidation };
}
